import { motion } from "framer-motion";
import { Check } from "lucide-react";

const FALLBACK = [
  {
    lookup_key: "single_session", name: "Single Session", price_display: "£90",
    price_sub: "per 55-minute session",
    description: "One private, fully-tailored Pilates session in your own home.",
    features: ["55 minutes, one-to-one", "Bespoke to your body & goals", "Equipment brought to you", "Flexible scheduling"],
    highlight: false,
  },
  {
    lookup_key: "package_11", name: "Package of 11 Sessions", price_display: "£900",
    price_sub: "for eleven private sessions",
    description: "A committed block of private sessions for consistent, visible progress.",
    features: ["Eleven one-to-one sessions", "Bespoke to your body & goals", "Equipment brought to you", "Structured, progressive programme"],
    highlight: true,
  },
];

const scrollTo = (id) => {
  const t = document.getElementById(id);
  if (!t) return;
  if (window.__lenis) window.__lenis.scrollTo(t, { offset: -80 });
  else t.scrollIntoView({ behavior: "smooth" });
};

export default function Pricing({ plans }) {
  const data = plans && plans.length ? plans : FALLBACK;

  return (
    <section id="pricing" data-testid="pricing-section" className="bg-[#FAF7F2] py-24 sm:py-32 lg:py-40">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-14">
        <div className="max-w-2xl mb-16">
          <p className="eyebrow label text-[#2D4A3E] mb-6">Pricing & packages</p>
          <h2 className="font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#1C1917]">
            Simple, private pricing.
          </h2>
          <p className="mt-5 text-[#57534E] text-lg leading-relaxed">
            Two ways to begin — a single session, or a committed block of eleven. Enquire to arrange your first visit.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {data.map((p, i) => (
            <motion.div
              key={p.lookup_key}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.12 }}
              data-testid={`pricing-card-${p.lookup_key}`}
              className={`relative rounded-3xl p-8 sm:p-10 flex flex-col transition-transform duration-500 hover:-translate-y-1.5 ${
                p.highlight
                  ? "bg-[#18181B] text-[#FAF7F2] grain shadow-[0_40px_80px_-40px_rgba(24,24,27,0.7)]"
                  : "bg-[#FFFFFF] text-[#1C1917] border border-[#1C1917]/10 shadow-[0_30px_60px_-45px_rgba(28,25,23,0.45)]"
              }`}
            >
              {p.highlight && (
                <span className="absolute top-8 right-8 label text-[#18181B] bg-[#C5A059] rounded-full px-4 py-1.5" data-testid="pricing-badge">
                  Best value
                </span>
              )}
              <p className={`label ${p.highlight ? "text-[#C5A059]" : "text-[#2D4A3E]"}`}>{p.name}</p>
              <div className="mt-6 flex items-end gap-3">
                <span className="font-display text-6xl sm:text-7xl leading-none">{p.price_display}</span>
              </div>
              {p.price_sub ? (
                <p className={`mt-3 font-mono-pp text-sm ${p.highlight ? "text-[#FAF7F2]/60" : "text-[#78716C]"}`}>
                  {p.price_sub}
                </p>
              ) : null}
              <div className={`mt-7 h-px ${p.highlight ? "bg-[#FAF7F2]/15" : "bg-[#1C1917]/10"}`} />
              <p className={`mt-6 text-base leading-relaxed ${p.highlight ? "text-[#FAF7F2]/75" : "text-[#57534E]"}`}>
                {p.description}
              </p>

              <ul className="mt-8 space-y-4 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-3">
                    <Check size={18} className={p.highlight ? "text-[#C5A059] mt-0.5" : "text-[#2D4A3E] mt-0.5"} />
                    <span className={`text-[0.95rem] ${p.highlight ? "text-[#FAF7F2]/85" : "text-[#57534E]"}`}>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                data-testid={`enquire-btn-${p.lookup_key}`}
                onClick={() => scrollTo("booking")}
                className={`mt-10 w-full rounded-full px-8 py-4 label btn-lux ${
                  p.highlight
                    ? "bg-[#C5A059] text-[#18181B] hover:bg-[#FAF7F2]"
                    : "bg-[#18181B] text-[#FAF7F2] hover:bg-[#2D4A3E]"
                }`}
              >
                Enquire about this
              </button>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
